import { useState, useEffect, useContext, useRef } from 'react';

import { LiveReserveContext } from './LiveLessonReseveContext';
import LiveReserveMaintenance from './maintenance/LiveReserveMaintenance';
import LiveReserveForm from './form/LiveReserveForm';
import Loading from '../../../common/loading/Loading';

import { requestApi } from '../../../../util/utility';

import { LiveReserveSituationDisplaySituations } from '../../../../models/lesson/live/reserve/situation/LiveReserveSituation';
import { LiveReserveInstructorModel } from '../../../../models/lesson/live/reserve/instructor/LiveReserveInstructor';


function LiveReserve() {
  // パラメータ(初回データ)
  const params = new URLSearchParams(window.location.search);
  const dateNow = params.get('date_now') || '';
  const dfInstId = params.get('inst_id') || '';
  // 不正な値が指定された場合はデフォルト値として 15 を設定
  const lessonTermMinute = 15;
  let dfLessonMinutes: number = parseInt(params.get('lesson_minutes') || String(lessonTermMinute), 10);
  if (![lessonTermMinute, lessonTermMinute*2, lessonTermMinute*3].includes(dfLessonMinutes)) {
    dfLessonMinutes = lessonTermMinute;
  }
  const [user, setUser] = useState<any>(null);
  const [config, setConfig] = useState(null);
  const [displaySituations, setLiveReserveSituationDisplaySituations] = useState<LiveReserveSituationDisplaySituations>([]);
  const [vars, setVars] = useState(null);
  const formRef = useRef<HTMLFormElement>(null);
  // 入力値
  const [instId, setInstId] = useState<string|null>(dfInstId);
  const [lsnMin, setLsnMin] = useState<number>(dfLessonMinutes);
  const [isTrial, setIsTrial] = useState<boolean>(true);
  const [selectedScheduleId, setSelectedScheduleId] = useState<string|null>(null);
  const [selectedSituation, setSelectedSituation] = useState<string|null>(null);
  const [requestContent, setRequestContent] = useState<string>('');
  // 出力値
  const [title, setTitle] = useState<string>('');
  const [coachingName, setCoachingName] = useState<string>('');
  const [isLiveMaintenance, setIsLiveMaintenance] = useState<boolean>(false);
  const [instructors, setInstructors] = useState<LiveReserveInstructorModel[]>([]);
  const [isCoaching, setIsCoaching] = useState<boolean>(false);
  const [isLesson, setIsLesson] = useState<boolean>(false);
  const [calendar, setCalendar] = useState([]);
  const [chkCal, setChkCal] = useState({});
  const [trialCount, setTrialCount] = useState<number | null>(null);
  const [isTicketLess, setIsTicketLess] = useState<boolean>(false);
  const [lessTicketCount, setLessTicketCount] = useState<number>(0);
  // 30分2チケット：アンケートで１チケットプレゼントキャンペーン
  const [isLsnSvyAddTicketTerm, setIsLsnSvyAddTicketTerm] = useState<boolean>(false);
  const [lsnSvyAddTicketStart, setLsnSvyAddTicketStart] = useState<Date | null>(null);
  const [lsnSvyAddTicketEnd, setLsnSvyAddTicketEnd] = useState<Date | null>(null);

  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);


  // サーバアクセス
  useEffect(() => {
    let cancelled = false;

    (async () => {
      setIsLoading(true);
      try {
        // API実行
        const resp = await requestApi({
          inc: '/backend/api/functions/user/lesson/live',
          date_now: dateNow,
          inst_id: instId,
          lesson_minutes: lsnMin,
        });
        if (cancelled) return;

        // 取得データの格納
        setLiveReserveSituationDisplaySituations(resp.displaySituations);
        setConfig(resp.config);
        setTitle(resp.title);
        setCoachingName(resp.COACHING_NAME);
        setVars(resp.vars);
        setUser(resp.user);
        setIsLiveMaintenance(resp.isLiveMaintenance);
        setInstructors(resp.instructor);
        setIsCoaching(resp.isCoaching);
        setIsLesson(resp.isLesson);
        setCalendar(resp.calendar);
        setChkCal(resp.chkCal);
        setTrialCount(resp.trialCount);
        setIsTicketLess(resp.isTicketLess);
        setLessTicketCount(resp.lessTicketCount);
        // 30分2チケット：アンケートで１チケットプレゼントキャンペーン
        const newLsnSvyAddTicketTerm = resp.LESSON_SURVEY_ADD_TICKET_TERM;
        const newLsnSvyAddTicketTermObj = typeof newLsnSvyAddTicketTerm === "string" ? JSON.parse(newLsnSvyAddTicketTerm) : newLsnSvyAddTicketTerm;
        if(newLsnSvyAddTicketTermObj?.START) {
          setLsnSvyAddTicketStart(new Date(newLsnSvyAddTicketTermObj.START));
        }
        if(newLsnSvyAddTicketTermObj?.END) {
          setLsnSvyAddTicketEnd(new Date(newLsnSvyAddTicketTermObj.END));
        }
        // 初期化
        setSelectedScheduleId(null);
      } catch(err) {
        if (err instanceof Error) {
          console.warn(err.message);
        }
      } finally {
        setIsFirstLoad(true);
        setTimeout(() => {
          setIsLoading(false);
        }, 100)
      }
    })();

    // cleanup関数でキャンセルフラグを立てる
    return () => {
      cancelled = true;
    };
  }, [instId, lsnMin])


  // 無料回数
  useEffect(() => {
    if(user?.register_items?.live_lesson) {
      setTrialCount(user?.register_items?.live_lesson);
    } else {
      setTrialCount(0);
    }
  }, [user])


  // 30分2チケット：アンケートで１チケットプレゼントキャンペーン
  useEffect(() => {
    let newIsLsnSvyAddTicketTerm = false;
    if(lsnSvyAddTicketStart && lsnSvyAddTicketEnd) {
      const now = new Date(); 
      newIsLsnSvyAddTicketTerm = Boolean(
        now >= lsnSvyAddTicketStart &&
        now < lsnSvyAddTicketEnd
      );
    }
    setIsLsnSvyAddTicketTerm(newIsLsnSvyAddTicketTerm);
  }, [lsnSvyAddTicketStart, lsnSvyAddTicketEnd])

  const contextVal = {
    lessonTermMinute: lessonTermMinute,
    user: user,
    config: config,
    vars: vars,
    setVars: setVars,
    title: title,
    coachingName: coachingName,
    isLiveMaintenance: isLiveMaintenance,
    isLesson: isLesson,
    isLoading: isLoading,
    formRef: formRef,
    // 講師データ
    instId: instId,
    setInstId: setInstId,
    instructors: instructors,
    // MT講師データ
    isCoaching: isCoaching,
    calendar: calendar,
    chkCal: chkCal,
    // 時間選択データ
    trialCount: trialCount,
    lsnMin: lsnMin,
    setLsnMin: setLsnMin,
    isTrial: isTrial,
    setIsTrial: setIsTrial,
    // 日時選択データ
    selectedScheduleId: selectedScheduleId,
    setSelectedScheduleId: setSelectedScheduleId,
    // シチュエーション
    displaySituations: displaySituations,
    selectedSituation: selectedSituation,
    setSelectedSituation: setSelectedSituation,
    // コメント
    requestContent: requestContent,
    setRequestContent: setRequestContent,
    // 送信
    isTicketLess: isTicketLess,
    lessTicketCount: lessTicketCount,
    // 30分2チケット：アンケートで１チケットプレゼントキャンペーン
    isLsnSvyAddTicketTerm: isLsnSvyAddTicketTerm,
    lsnSvyAddTicketStart: lsnSvyAddTicketStart,
    lsnSvyAddTicketEnd: lsnSvyAddTicketEnd,
  }


  return (
    <LiveReserveContext.Provider value={contextVal}>
      {isFirstLoad ?
        <div style={{margin: '40px 0'}}>
          <h3 id="live_lesson_reservation" className="midashi1 bold">{title}を予約する</h3>

          {isLiveMaintenance ?
            <LiveReserveMaintenance />
          :
            <LiveReserveForm />
          }

        </div>
      :
        <Loading />
      }
    </LiveReserveContext.Provider>
  );
}
  
export default LiveReserve;
  